/*
 * Registro de actividad local del panel (developer.activityLog). Plan: design/SETTINGS-DEVELOPER-PRIVACY.md 5.1.
 *
 * Solo en este navegador (IndexedDB del panel): nada se envia al servidor, que ya tiene su propia auditoria.
 * Cada entrada dice que paso, cuando, donde y con que item (nombre e id), nunca el valor. Tope de 1000 entradas;
 * al llegar se descartan las mas antiguas. Cada entrada lleva la cuenta para que otra cuenta del mismo navegador
 * no vea la actividad de la anterior. La extension tiene el suyo (browser-extension/activity-log.js) y la
 * pestaña Developer junta los dos.
 *
 * Solo anota si los ajustes de la cuenta lo piden (developer.enabled y developer.activityLog).
 */
import { ensureAccountSettings } from '../hooks/useAccountSettings';

// copy_secret / reveal_secret / export_private_key: la clave privada y la passphrase de un item SSH.
export const KINDS = ['fill', 'fill_otp', 'copy_password', 'reveal_password', 'unlock', 'copy_secret', 'reveal_secret', 'export_private_key'];
export const MAX_ENTRIES = 1000;
const DB_NAME = 'sgc-activity';
const STORE = 'events';

let dbPromise = null;
function open() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') { reject(new Error('indexeddb_unavailable')); return; }
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => { dbPromise = null; reject(req.error); };
    });
  }
  return dbPromise;
}

const done = (tx) => new Promise((resolve, reject) => {
  tx.oncomplete = () => resolve();
  tx.onerror = () => reject(tx.error);
  tx.onabort = () => reject(tx.error);
});
const result = (req) => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error); });

// eslint-disable-next-line no-control-regex
const clip = (value, max) => String(value == null ? '' : value).replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max);

/** La entrada tal como se guarda: solo los campos de la lista, recortados; null si el tipo no existe. */
export function buildEntry({ kind, itemId, itemName, origin, client = 'panel', account }, now = new Date()) {
  if (!KINDS.includes(kind)) return null;
  return {
    at: now.toISOString(),
    kind,
    client: clip(client, 40),
    origin: clip(origin, 255),
    itemId: clip(itemId, 64),
    itemName: clip(itemName, 200),
    account: clip(account, 255),
  };
}

/** Guarda una entrada sin mas comprobaciones (los ajustes los mira `record`). */
export async function add(fields) {
  const entry = buildEntry(fields);
  if (!entry) return false;
  const db = await open();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  store.add(entry);
  const total = await result(store.count());
  if (total > MAX_ENTRIES) {
    let toDrop = total - MAX_ENTRIES;
    await new Promise((resolve, reject) => {
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor && toDrop > 0) { cursor.delete(); toDrop -= 1; cursor.continue(); } else resolve();
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
  }
  await done(tx);
  return true;
}

/**
 * Anota un evento de este panel si el registro esta encendido. Nunca rompe lo que el usuario estaba haciendo.
 * `fields`: { itemId, itemName, origin }. Nunca pasar un valor (contraseña, codigo, secreto).
 */
export async function record(kind, fields = {}) {
  try {
    const settings = await ensureAccountSettings();
    if (!settings?.developer?.enabled || !settings.developer.activityLog) return false;
    return await add({ ...fields, kind, client: 'panel', account: localStorage.getItem('rfidId') || '' });
  } catch {
    return false;
  }
}

/** Entradas de la cuenta de este navegador, de la mas reciente a la mas antigua. */
export async function list(account = localStorage.getItem('rfidId') || '') {
  const db = await open();
  const all = await result(db.transaction(STORE, 'readonly').objectStore(STORE).getAll());
  return all.filter((e) => e.account === account).reverse();
}

/** Borra todo el registro de este navegador. */
export async function clear() {
  try {
    const db = await open();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    await done(tx);
  } catch { /* sin IndexedDB no hay nada que borrar */ }
}

const activityLog = { KINDS, MAX_ENTRIES, buildEntry, add, record, list, clear };
export default activityLog;
